import {
  FormFieldDefinition,
  SignupFormDefinition,
} from '../../runtypes/SignupFormDefinitionRuntype';

export const ID_FIELD_NAME = '000100bbf7a5329ffa85abbb84c0c5e3';
export const ID_FIELD_CONTACT = '0002009da13505e1ece0064fcc73e28a';
export const ID_FIELD_EMAIL = '00030085e3dc06d6c57fe7a51c55813e';
export const ID_FIELD_MARKETS = '000400c4beab3e757e2cf519c3d21cde';
export const ID_FIELD_CATEGORY = '0005b5220f6b5c8d1a8be40ab34cd082';
export const ID_FIELD_PRODUCTS = '000600b0c587bc1b046d6a1974b4b2e1';
export const ID_FIELD_BUSINESS_DESCRIPTION = '000700cb606f0d8c2f5e8da176d8c19c';
export const ID_FIELD_BOOTH_IMAGE = '0008002da6674272a68e0565e2491535';
export const ID_FIELD_PRODUCT_IMAGE_1 = '000900fefc04c45226e0e336d992515b';
export const ID_FIELD_PRODUCT_IMAGE_2 = '0010003414b4c75356a5857158a00112';
export const ID_FIELD_PRODUCT_IMAGE_3 = '00110022c1e99bc62dee47a291ce6b7c';
export const ID_FIELD_TERMS = '001200e7b9f23ce3f8490ef78ef542b9';
export const ID_FIELD_AGREE_TO_TERMS = '001300167bacf314455fcdc43c8c6978';

export const APP_FIELDS = {
  ID_FIELD_NAME,
  ID_FIELD_CONTACT,
  ID_FIELD_EMAIL,
  ID_FIELD_MARKETS,
  ID_FIELD_CATEGORY,
};

function defineFormFields<T extends string>(
  obj: Record<T, FormFieldDefinition>,
): { [K in T]: FormFieldDefinition } {
  return obj;
}

export const defaultSignupFields = defineFormFields({
  name: {
    id: ID_FIELD_NAME,
    type: 'TextElement',
    name: 'Name',
    label: 'Business Name',
    hidden: false,
    is_required: true,
    is_public: true,
  },
  contact: {
    id: ID_FIELD_CONTACT,
    type: 'TextElement',
    name: 'Contact Person',
    label: 'Contact Person',
    hidden: false,
    is_required: true,
    is_public: true,
  },
  email: {
    id: ID_FIELD_EMAIL,
    type: 'TextElement',
    name: 'Email',
    label: 'Email Address',
    hidden: false,
    is_required: true,
    is_public: false,
  },
  markets: {
    id: ID_FIELD_MARKETS,
    type: 'MarketSelectorElement',
    name: 'Market(s)',
    label: 'Choose Market(s)',
    hidden: false,
    disableSelectAll: false,
    calendarView: false,
    hiddenInForm: false,
    is_required: true,
    is_public: true,
  },
  category: {
    id: ID_FIELD_CATEGORY,
    type: 'MultichoiceElement',
    name: 'Category',
    label: 'Category',
    options: [
      // TODO: Change these IDs?
      { id: '0', name: 'Farmer' },
      { id: '1', name: 'Prepared-Food Vendor' },
      { id: '2', name: 'Non-Food Vendor' },
    ],
    allow_multi: false,
    hidden: false,
    is_required: true,
    is_public: true,
  },
  products: {
    id: ID_FIELD_PRODUCTS,
    type: 'TextElement',
    name: 'Products',
    label:
      'Please list the products you would like to sell at the market. These will be searchable on our market map.',
    hidden: false,
    is_required: true,
    is_public: false,
    multiline: true,
  },
  businessDescription: {
    id: ID_FIELD_BUSINESS_DESCRIPTION,
    type: 'TextElement',
    name: 'Business Description',
    label:
      'Please provide a robust Business Description. This will form part of your public profile on our website.',
    hidden: false,
    is_required: true,
    is_public: false,
    multiline: true,
  },
  boothImage: {
    id: ID_FIELD_BOOTH_IMAGE,
    type: 'FileUploadElement',
    name: 'Booth Image',
    label: 'Booth Image',
    hidden: false,
    is_required: false,
    image_only: true,
    is_public: false,
  },
  productImage1: {
    id: ID_FIELD_PRODUCT_IMAGE_1,
    type: 'FileUploadElement',
    name: 'Product Image #1',
    label: 'Product Image #1',
    hidden: false,
    is_required: false,
    image_only: true,
    is_public: false,
  },
  productImage2: {
    id: ID_FIELD_PRODUCT_IMAGE_2,
    type: 'FileUploadElement',
    name: 'Product Image #2',
    label: 'Product Image #2',
    hidden: false,
    is_required: false,
    image_only: true,
    is_public: false,
  },
  productImage3: {
    id: ID_FIELD_PRODUCT_IMAGE_3,
    type: 'FileUploadElement',
    name: 'Product Image #3',
    label: 'Product Image #3',
    hidden: false,
    is_required: false,
    image_only: true,
    is_public: false,
  },
  terms: {
    id: ID_FIELD_TERMS,
    type: 'ContentElement',
    format: 'html',
    text: `<p><strong>Terms and Conditions</strong></p><p>This is an example of terms and conditions that your market may require applicants to agree to:</p><p><strong>I am stating that I understand that this application is not a guarantee to vend at the Yourtown Sunday Market, that my application and supporting materials will be reviewed by the market Board of Directors and that I will be notified upon acceptance or denial. I understand that the Yourtown Sunday Market reserves the right to deny any vendor for any reason that is not protected by standard non-discrimination laws. Furthermore I understand that if accepted, I will be required to provide a copy of my business license and other required permits, as well as a certificate of insurance listing the Yourtown Sunday Market as an additional insured before I will be allowed to sell at the market.</strong></p>`,
  },
  agreeToTerms: {
    id: ID_FIELD_AGREE_TO_TERMS,
    type: 'BooleanElement',
    name: 'Accept',
    label:
      'I have read and accept the preceding terms, conditions, rules and regulations',
    hidden: false,
    require_acceptance: true,
    is_public: false,
    default_value: false,
    use_checkbox: false,
  },
});

export const DefaultSignupForm: SignupFormDefinition = {
  id: 'signup',
  name: 'Vendor Signup Form',
  enabled: true,
  fields: [
    defaultSignupFields.name,
    defaultSignupFields.contact,
    defaultSignupFields.email,
    defaultSignupFields.markets,
    defaultSignupFields.category,
    defaultSignupFields.products,
    defaultSignupFields.businessDescription,
    defaultSignupFields.boothImage,
    defaultSignupFields.productImage1,
    defaultSignupFields.productImage2,
    defaultSignupFields.productImage3,
    defaultSignupFields.terms,
    defaultSignupFields.agreeToTerms,
  ],
  display_price: true,
  post_submit_content:
    '<p>Thank you. Your information has been submitted successfully.</p>',
};
